import memoize from "lodash.memoize";
import { MidiNote, Note, noteToMidi } from "./notes";

export enum PredefinedRangeType {
  Bass = "bass",
  Baritone = "baritone",
  Tenor = "tenor",
  Alto = "alto",
  MezzoSoprano = "mezzo-soprano",
  Soprano = "soprano",
}

export interface Range {
  range: PredefinedRangeType | "custom";
  low: Note;
  high: Note;
}

function makeRange(range: PredefinedRangeType, low: Note, high: Note): Range {
  return {
    range,
    low,
    high,
  };
}

export const predefinedRanges: { [key in PredefinedRangeType]: Range } = {
  bass: makeRange(PredefinedRangeType.Bass, Note.e2, Note.e4),
  baritone: makeRange(PredefinedRangeType.Baritone, Note.g2, Note.g4),
  tenor: makeRange(PredefinedRangeType.Tenor, Note.c3, Note.c5),
  alto: makeRange(PredefinedRangeType.Alto, Note.e3, Note.f5),
  "mezzo-soprano": makeRange(
    PredefinedRangeType.MezzoSoprano,
    Note.f3,
    Note.a5
  ),
  soprano: makeRange(PredefinedRangeType.Soprano, Note.a3, Note.c6),
};

export const rangeToMidi = memoize(function rangeToMidi(
  range: Range
): [number, number] {
  return [noteToMidi(range.low), noteToMidi(range.high)];
});

export function isNoteWithinRange(midiNote: MidiNote, range: Range) {
  return isNoteWithinMidiRange(midiNote, rangeToMidi(range));
}

export function isNoteWithinMidiRange(
  midiNote: MidiNote,
  [startMidi, endMidi]: [number, number]
) {
  return midiNote >= startMidi && midiNote <= endMidi;
}

export function middleMidiNoteOfRange(range: Range) {
  const [startMidi, endMidi] = rangeToMidi(range);

  const middle = Math.floor((startMidi + endMidi) / 2);

  if (middle < startMidi) {
    return startMidi;
  }

  if (middle > endMidi) {
    return endMidi;
  }

  return middle;
}

export function isPredefinedRangeType(str: string): str is PredefinedRangeType {
  if (!str) {
    return false;
  }

  return Object.values(PredefinedRangeType).includes(
    str as PredefinedRangeType
  );
}
